// ============================================
// SPECIALTY PRIORITIES
// Defines which templates appear first for each specialty
// ============================================

export const specialtyPriorities: Record<string, string[]> = {
  // ============================================
  // CARDIOLOGY
  // ============================================
  cardiology: [
    // Medications
    "entresto",
    "pcsk9",
    "eliquis_afib",
    "sglt2_hf",
    // Procedures
    "cardiac_cath",
    "pci",
    "ablation_afib",
    "pacemaker",
    "icd",
    "watchman",
    "tavr",
    // Imaging
    "echo",
    "stress_echo",
    "nuclear_stress",
    "cta_coronary",
    "cardiac_mri",
    // Referrals
    "cardiac_rehab",
  ],

  // ============================================
  // ORTHOPEDICS
  // ============================================
  orthopedics: [
    // Procedures
    "tka",
    "tha",
    "tsa",
    "lumbar_fusion",
    "cervical_fusion",
    "laminectomy",
    "arthroscopy_knee",
    "arthroscopy_shoulder",
    "rotator_cuff_repair",
    "acl_reconstruction",
    "carpal_tunnel_release",
    // Imaging
    "mri_knee",
    "mri_shoulder",
    "mri_lumbar",
    "mri_cervical",
    "ct_spine",
    // DME
    "knee_brace",
    "back_brace",
    "bone_stimulator",
  ],

  // ============================================
  // NEUROLOGY
  // ============================================
  neurology: [
    // Medications
    "ms_medication",
    "migraine_cgrp",
    "botox_migraine",
    "antiepileptic",
    // Procedures
    "emg",
    "eeg",
    "vns",
    "lumbar_puncture",
    // Imaging
    "mri_brain",
    "mri_cervical",
    "mra_brain",
    "ct_head",
    // DME
    "cpap",
  ],

  // ============================================
  // GASTROENTEROLOGY
  // ============================================
  gastroenterology: [
    // Medications
    "biologic_ibd",
    "linzess",
    "hep_c_treatment",
    // Procedures
    "colonoscopy",
    "egd",
    "ercp",
    "eus",
    "capsule_endoscopy",
    // Imaging
    "ct_abdomen",
    "mrcp",
    "mre",
  ],

  // ============================================
  // PULMONOLOGY
  // ============================================
  pulmonology: [
    // Medications
    "biologic_asthma",
    "trelegy",
    "pulm_htn_meds",
    // Procedures
    "sleep_study",
    // Imaging
    "ct_chest",
    "hrct_chest",
    // DME
    "cpap",
    "bipap",
    "home_oxygen",
    "portable_oxygen",
    "nebulizer",
    // Referrals
    "pulmonary_rehab",
  ],

  // ============================================
  // RHEUMATOLOGY
  // ============================================
  rheumatology: [
    // Medications
    "biologic_ra",
    "biologic_psoriasis",
    "biologic_as",
    "jak_inhibitor",
    // Imaging
    "mri_hand",
    "mri_foot",
    "xray_hands",
  ],

  // ============================================
  // ONCOLOGY
  // ============================================
  oncology: [
    // Medications
    "immunotherapy",
    "targeted_therapy",
    "antiemetic_chemo",
    "growth_factor",
    // Procedures
    "genetic_testing",
    // Imaging
    "pet_ct",
    "ct_chest_abdomen_pelvis",
    "mri_brain",
    // Referrals
    "oncology_second_opinion",
  ],

  // ============================================
  // OB/GYN
  // ============================================
  obgyn: [
    // Medications
    "iud",
    "nexplanon",
    "orilissa",
    "lupron_gyn",
    "letrozole_fertility",
    "progesterone_support",
    "clomid",
    "makena",
    // Procedures
    "hysterectomy",
    "myomectomy",
    "endometrial_ablation",
    "colposcopy",
    "leep",
    "dnc",
    "laparoscopy_endo",
    "iui",
    "fertility_ivf",
    // Imaging
    "pelvic_ultrasound",
    "hsg",
    "mri_pelvis",
  ],

  // ============================================
  // UROLOGY
  // ============================================
  urology: [
    // Medications
    "myrbetriq",
    "cialis_bph",
    "xtandi",
    // Procedures
    "prostatectomy",
    "turp",
    "urolift",
    "cystoscopy",
    "lithotripsy",
    "interstim",
    // Imaging
    "ct_urogram",
    "mri_prostate",
    "renal_ultrasound",
  ],

  // ============================================
  // PAIN MANAGEMENT
  // ============================================
  pain_management: [
    // Medications
    "lidocaine_patch",
    "qutenza",
    // Procedures
    "epidural_injection",
    "facet_injection",
    "si_joint_injection",
    "rfa",
    "spinal_cord_stim",
    "intrathecal_pump",
    "trigger_point_injection",
    // Imaging
    "mri_lumbar",
    "mri_cervical",
  ],

  // ============================================
  // PSYCHIATRY
  // ============================================
  psychiatry: [
    // Medications
    "ketamine_esketamine",
    "brand_antipsychotic",
    "lai_antipsychotic",
    "adhd_brand",
    // Procedures
    "tms",
    "ect",
    "pharmacogenomic_testing",
    // Referrals
    "inpatient_psych",
    "php",
    "iop",
    "residential_treatment",
  ],

  // ============================================
  // DERMATOLOGY
  // ============================================
  dermatology: [
    // Medications
    "biologic_eczema",
    "biologic_psoriasis",
    "opzelura",
    "isotretinoin",
    // Procedures
    "mohs",
    "phototherapy",
  ],

  // ============================================
  // ENT
  // ============================================
  ent: [
    // Medications
    "dupixent_crswnp",
    // Procedures
    "tonsillectomy",
    "sinus_surgery",
    "septoplasty",
    "cochlear_implant",
    "ear_tubes",
    // Imaging
    "ct_sinus",
    "mri_iac",
  ],

  // ============================================
  // ENDOCRINOLOGY
  // ============================================
  endocrinology: [
    // Medications
    "glp1_t2dm",
    "glp1_obesity",
    "sglt2_hf",
    "sglt2_ckd",
    // DME
    "cgm",
    "insulin_pump",
  ],

  // ============================================
  // NEPHROLOGY
  // ============================================
  nephrology: [
    // Medications
    "sglt2_ckd",
    "epo_agents",
    "phosphate_binder",
    // Procedures
    "dialysis_access",
    // DME
    "home_dialysis",
  ],

  // ============================================
  // ALLERGY / IMMUNOLOGY (EXPANDED)
  // ============================================
  allergy_immunology: [
    // Medications
    "biologic_asthma",
    "biologic_eczema",
    "xolair_urticaria",
    "slit_tablets",
    "epipen",
    "ivig_immunodeficiency",
    "scig",
    "hereditary_angioedema",
    "hae_acute",
    // Procedures
    "allergy_skin_testing",
    "scit_immunotherapy",
    "food_challenge",
    "food_allergy_testing",
    "drug_allergy_testing",
    "venom_immunotherapy",
    "component_testing",
  ],

  // ============================================
  // INFECTIOUS DISEASE (EXPANDED)
  // ============================================
  infectious_disease: [
    // Medications
    "hep_c_treatment",
    "hiv_prep",
    "opat",
    "antifungal_systemic",
    "ivig_infection",
    "cmv_treatment",
    "mac_treatment",
    "tb_treatment",
    "chronic_hbv",
    "pep_hiv",
    "clostridioides",
    "dalbavancin",
    // Procedures
    "picc_line",
    "midline_catheter",
    "fmt",
    // Referrals
    "id_consult",
    "travel_medicine",
  ],

  // ============================================
  // PEDIATRICS (EXPANDED)
  // ============================================
  pediatrics: [
    // Medications
    "growth_hormone",
    "biologic_jia",
    "adhd_nonstim",
    "adhd_brand",
    "asthma_biologic_peds",
    "synagis",
    "nirsevimab",
    "epipen_peds",
    "peds_eczema_biologic",
    // Procedures
    "gtube_placement",
    "palate_repair",
    "cleft_lip_repair",
    "adenoidectomy",
    "tongue_tie_release",
    "orchiopexy",
    "hypospadias_repair",
    "circumcision_medical",
    "ear_tubes",
    "tonsillectomy",
    // Referrals
    "peds_feeding_therapy",
    "peds_ot",
    "peds_pt",
    "peds_speech",
    "autism_eval",
    "neuropsych_eval_peds",
    "early_intervention",
    "aba_therapy",
    // DME
    "nebulizer",
    "apnea_monitor",
  ],

  // ============================================
  // PM&R / PHYSICAL MEDICINE
  // ============================================
  pm_r: [
    // Procedures
    "epidural_injection",
    "facet_injection",
    "si_joint_injection",
    "rfa",
    "trigger_point_injection",
    "emg",
    // Referrals
    "inpatient_rehab",
    "outpatient_pt",
    "outpatient_ot",
    "speech_therapy",
    // DME
    "wheelchair",
    "power_wheelchair",
    "hospital_bed",
    "back_brace",
  ],

  // ============================================
  // GENERAL SURGERY
  // ============================================
  general_surgery: [
    // Procedures
    "bariatric",
    "cholecystectomy",
    "hernia_repair",
    // Imaging
    "ct_abdomen",
  ],

  // ============================================
  // VASCULAR SURGERY
  // ============================================
  vascular_surgery: [
    // Procedures
    "carotid_endarterectomy",
    "dialysis_access",
    // Imaging
    "cta_chest",
    "venous_duplex",
    "arterial_duplex",
    // Referrals
    "wound_care",
  ],

  // ============================================
  // INTERNAL MEDICINE / PRIMARY CARE
  // ============================================
  internal_medicine: [
    // Medications
    "glp1_t2dm",
    "glp1_obesity",
    "pcsk9",
    "eliquis_afib",
    // DME
    "cpap",
    "cgm",
    // Referrals
    "home_health",
    "snf",
  ],

  // ============================================
  // FAMILY MEDICINE
  // ============================================
  family_medicine: [
    // Medications
    "glp1_t2dm",
    "glp1_obesity",
    "iud",
    "nexplanon",
    // DME
    "cpap",
    "cgm",
    // Referrals
    "home_health",
    "outpatient_pt",
  ],

  // ============================================
  // GERIATRICS
  // ============================================
  geriatrics: [
    // DME
    "wheelchair",
    "hospital_bed",
    "walker",
    // Referrals
    "home_health",
    "snf",
    "hospice",
    "outpatient_pt",
    "outpatient_ot",
  ],
};

// Alias for backward compatibility
export const SPECIALTY_TEMPLATE_PRIORITIES = specialtyPriorities;

// Helper function to get priority templates for a specialty
export function getPriorityTemplatesForSpecialty(specialtyId: string): string[] {
  return specialtyPriorities[specialtyId] || [];
}

// Helper function to check if a template is prioritized for a specialty
export function isTemplatePrioritizedForSpecialty(
  templateId: string,
  specialtyId: string
): boolean {
  const priorities = specialtyPriorities[specialtyId];
  return priorities ? priorities.includes(templateId) : false;
}